import * as Accordion from "@radix-ui/react-accordion";
import * as Dialog from "@radix-ui/react-dialog";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { PlayCircle, X } from "lucide-react";
import { useState } from "react";

const features = [
  {
    id: "login-registration",
    school: "yes",
    description: "Register students, teachers, parents and admins.",
    title: "Login & Registration",
    media: [
      {
        type: "video",
        title:
          "First Time Login to create PIN and Password in a few simple steps.",
        description: "First Time Student Login",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Student_Registration.mp4",
      },
    ],
  },
  {
    id: "timetable-lecture-management",
    school: "yes",
    description: "Create and manage timetables, lectures, and schedules.",
    title: "Timetable & Lecture Management",
    media: [
      {
        type: "video",
        title:
          "Manage and Track lecture instructions, lecture summary, shared files, online class, recordings, assignments, and assessments effortlessly.",
        description: "Lecture Summary Management",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Lecture_Summary_Feature.mp4",
      },
    ],
  },
  {
    id: "online-class",
    school: "yes",
    description:
      "Conduct live online classes seamlessly with integrated teaching tools and student access.",
    title: "Online Class",
    media: [
      {
        type: "video",
        title:
          "Conduct live classes where students and teachers can communicate both ways with enriched features.",
        description: "2 Way",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Online_Class_Feature_2_Way.mp4",
      },
      {
        type: "image",
        title:
          "Conduct smooth one-way classes where teachers teach and students attend without interruptions.",
        description: "1 Way",
        url: "To Prepare",
      },
    ],
  },
  {
    id: "content-library-management",
    school: "yes",
    description: "Organize and Manage curriculum contents.",
    title: "Content Library Management",
    media: [
      {
        type: "video",
        title:
          "Access enriched interactive multimedia curriculum contents. Manage notes, bookmarks and queries.",
        description: "Content Library",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Content_Library_View_Content_Personal_Library.mp4",
      },
    ],
  },
  {
    id: "assessment-management",
    school: "yes",
    description:
      "Create tests and assessments, assign them to students, and track results instantly.",
    title: "Assessment Management",
    media: [
      {
        type: "image",
        title:
          "Conduct assessments in multiple formats to match different learning needs.",
        description: "Assessment 3 modes",
        url: "To Prepare",
      },
      {
        type: "video",
        title:
          "Plan, schedule, and manage the complete assessment process end-to-end.",
        description: "Assessment Process",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Assessment_Process.mp4",
      },
      {
        type: "video",
        title:
          "Deliver personalized assessments based on each student's learning level.",
        description: "Adaptive Assessment",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Adaptive_Assessment_Process.mp4",
      },
    ],
  },
  {
    id: "performance-management",
    school: "yes",
    description:
      "Monitor student progress with detailed performance reports and learning insights.",
    title: "Performance Management",
    media: [
      {
        type: "image",
        title:
          "Get intelligent performance insights and progress tracking for every student.",
        description: "Smart Performance",
        url: "To Prepare",
      },
      {
        type: "image",
        title:
          "Record and manage offline test scores and classroom performance easily.",
        description: "Offline Performance",
        url: "To Prepare",
      },
    ],
  },
  {
    id: "assignment-management",
    school: "yes",
    description:
      "Assign homework, Pre-reads and activities digitally, collect submissions, and review everything in one workflow.",
    title: "Assignment Management",
    media: [
      {
        type: "video",
        title:
          "Assign homework digitally, collect submissions, and review student work easily.",
        description: "Homework",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Homework_Process.mp4",
      },
      {
        type: "image",
        title:
          "Share pre-class learning material so students come prepared for the lecture.",
        description: "PreRead",
        url: "To Prepare",
      },
      {
        type: "image",
        title:
          "Create engaging learning activities to boost participation and understanding.",
        description: "Activity",
        url: "To Prepare",
      },
    ],
  },

  {
    id: "doubts-platform",
    school: "yes",
    description:
      "Students ask doubts anytime and get quick responses from peers, teachers or mentors.",
    title: "Doubts Platform",
    media: [
      {
        type: "video",
        title:
          "Allow students to raise doubts anytime and get structured responses.",
        description: "Query Platform",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Query_Platform.mp4",
      },
      {
        type: "image",
        title:
          "Provide instant support and answers to students using AI-powered assistance.",
        description: "AI Chatbot",
        url: "To Prepare",
      },
    ],
  },
  {
    id: "study-planner",
    school: "yes",
    description: "Plan study goals for students to stay consistent and ahead.",
    title: "Study Planner",
    media: [
      {
        type: "video",
        title:
          "Help students plan their studies with daily and weekly learning schedules.",
        description: "Study Planner",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Study_Planner.mp4",
      },
    ],
  },
  {
    id: "self-assessment",
    school: "yes",
    description:
      "Enable students to test themselves regularly and identify improvement areas.",
    title: "Self-Assessment",
    media: [
      {
        type: "video",
        title: "Let students evaluate themselves regularly to improve faster.",
        description: "Self-Assessment",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Self-Assessment.mp4",
      },
    ],
  },
  {
    id: "attendance-management",
    school: "yes",
    description:
      "Mark and track attendance digitally for students in seconds, Notify shortages and reports.",
    title: "Attendance Management",
    media: [
      {
        type: "video",
        title:
          "Mark and manage attendance digitally for students in seconds. Notify shortages and reports.",
        description: "Attendance",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Attendence_Mgt_Feature.mp4",
      },
    ],
  },
  {
    id: "notice-management",
    school: "yes",
    description:
      "Share important announcements instantly with students, parents, and staff with auto-alerts.",
    title: "Notice Management",
    media: [
      {
        type: "image",
        title:
          "Send official notices and circulars to students and parents instantly.",
        description: "Notice & Circulars",
        url: "To Prepare",
      },
      {
        type: "image",
        title:
          "Deliver important reminders and updates automatically to the right audience.",
        description: "Alerts",
        url: "To Prepare",
      },
    ],
  },
  {
    id: "engagement-community",
    school: "yes",
    description:
      "Build a learning community where students and teachers can interact and stay engaged.",
    title: "Engagement & Community",
    media: [
      {
        type: "video",
        title:
          "Build a learning community where students and teachers stay connected and engaged.",
        description: "Community",
        url: "https://publicbucket.breathe4school.ai/Feature-Guide-Videos/Engagement_Community.mp4",
      },
    ],
  },
  {
    id: "issues-voc-management",
    school: "yes",
    description:
      "Track requests, and voice-of-customer issues with a structured resolution system.",
    title: "Issues / VOC Management",
    media: [
      {
        type: "image",
        title:
          "Capture issues, and requests and manage resolution systematically.",
        description: "Issues / VOC",
        url: "To Prepare",
      },
    ],
  },
  {
    id: "institutional-branding",
    school: "yes",
    description:
      "Customize your institute's identity with branding elements for a professional experience.",
    title: "Institutional Branding",
    media: [
      {
        type: "image",
        title:
          "Create and share high-performing posts across social media to boost reach and engagement quickly",
        description: "Making the Post Viral",
        url: "To Prepare",
      },
    ],
  },
];

/* ================= COMPONENT ================= */
export default function UserGuidePage() {
  const [open, setOpen] = useState(false);
  const [activeMedia, setActiveMedia] = useState(null);

  const handleMediaClick = (item) => {
    if (item.url === "To Prepare") return;
    setActiveMedia(item);
    setOpen(true);
  };

  return (
    <div className="h-[calc(100vh-100px)] w-[90vw] mx-auto py-10">
      {/* ===== PAGE SCROLL (VERTICAL) - scrollbar-gutter added ===== */}
      <ScrollArea.Root className="h-full w-full overflow-hidden orange-scrollBar">
        <ScrollArea.Viewport className="h-full w-full overflow-y-auto scrollbar-gutter-stable orange-scrollBar">
          <div className="mx-auto w-full px-4 py-6 md:px-6">
            {/* Heading - Fixed width container mein wrap kiya */}
            <div className="mb-10 text-center w-full">
              <h1 className="text-2xl font-bold text-orange-600 md:text-3xl">
                Guide for Every Feature
              </h1>
              <p className="mt-2 text-sm text-gray-500 md:text-base">
                Learn how to use every feature step-by-step with visual guidance
              </p>
            </div>

            {/* Accordion - Fixed width constraints */}
            <Accordion.Root
              type="single"
              collapsible
              className="space-y-4 w-[1325px]"
            >
              {features.map((feature) => (
                <Accordion.Item
                  key={feature.id}
                  value={feature.id}
                  className="rounded-xl border border-orange-200 bg-white shadow-sm w-full overflow-hidden"
                >
                  <Accordion.Header>
                    <Accordion.Trigger className="w-full px-4 py-4 text-left md:px-6 [&[data-state=open]_.helper-text]:hidden">
                      <div className="flex flex-col gap-1">
                        <span className="text-base font-semibold text-orange-600 md:text-lg">
                          {feature.title}
                        </span>
                        <span className="helper-text text-xs text-gray-500 md:text-sm">
                          Click to view detailed guide
                        </span>
                      </div>
                    </Accordion.Trigger>
                  </Accordion.Header>

                  <Accordion.Content className="w-full px-4 pb-6 md:px-6 data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
                    <div className="rounded-xl border border-dashed border-orange-200 bg-white p-4 md:p-6 w-full">
                      {/* ===== MEDIA SCROLL - Fixed width with internal scroll ===== */}
                      <div className="w-full overflow-x-auto overflow-y-hidden orange-scrollBar pb-4">
                        <div className="flex flex-nowrap gap-4 md:gap-6 w-max min-w-full">
                          {feature.media.map((item, index) => (
                            <div
                              key={index}
                              onClick={() => handleMediaClick(item)}
                              className={`w-[280px] md:w-[300px] flex-shrink-0 rounded-2xl border border-orange-200 bg-orange-50/40 p-4 md:p-5 transition-all duration-300 hover:bg-orange-50 ${
                                item.url !== "To Prepare"
                                  ? "cursor-pointer hover:shadow-lg hover:shadow-orange-200"
                                  : "cursor-default opacity-70"
                              }`}
                            >
                              {/* Placeholder */}
                              <div className="relative mb-4 h-40 md:h-44 w-full overflow-hidden rounded-xl bg-gradient-to-br from-orange-500/25 via-orange-200/30 to-orange-400/20">
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.35),transparent_65%)]" />

                                {item.type === "video" &&
                                  item.url !== "To Prepare" && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-full bg-white shadow-2xl">
                                        <PlayCircle className="h-8 w-8 md:h-10 md:w-10 text-orange-600" />
                                      </div>
                                    </div>
                                  )}

                                <span className="absolute bottom-3 right-3 rounded-md bg-black/65 px-2.5 py-0.5 text-[11px] text-white">
                                  {item.type === "video"
                                    ? "Video demo"
                                    : "Image"}
                                </span>
                              </div>

                              {/* Text */}
                              <h4 className="mb-1.5 text-center text-sm md:text-base font-semibold text-orange-700 line-clamp-2">
                                {item.description}
                              </h4>

                              <p className="text-center text-xs md:text-sm text-gray-600 line-clamp-2">
                                {item.title}
                              </p>

                              {item.url === "To Prepare" && (
                                <div className="mt-3 text-center text-[11px] animate-pulse uppercase tracking-wider text-orange-500">
                                  Coming Soon
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </div>
        </ScrollArea.Viewport>

        {/* Page Vertical Scrollbar */}
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="w-2.5 bg-orange-100 rounded-full"
        >
          <ScrollArea.Thumb className="bg-orange-400 rounded-full" />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />

          <Dialog.Content
            onInteractOutside={(e) => e.preventDefault()}
            className="fixed left-1/2 top-1/2 w-[95vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto"
          >
            {/* Cross Button */}
            <Dialog.Close asChild>
              <button className="absolute right-4 top-4 rounded-full p-2 hover:bg-orange-100 transition">
                <X className="h-5 w-5 text-orange-600" />
              </button>
            </Dialog.Close>

            {activeMedia && (
              <>
                <div className="px-4 md:px-6 pt-6">
                  <Dialog.Title className="text-lg font-semibold text-orange-600">
                    {activeMedia.description}
                  </Dialog.Title>
                  <p className="text-sm text-gray-600 mt-1">
                    {activeMedia.title}
                  </p>
                </div>

                <div className="p-4 md:p-6">
                  {activeMedia.type === "video" ? (
                    <video
                      src={activeMedia.url}
                      controls
                      autoPlay
                      className="w-full rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-64 md:h-96 bg-orange-100 rounded-lg flex items-center justify-center">
                      Image Preview
                    </div>
                  )}
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
